// The pattern library is a Firestore collection ("library") where each document
// represents a community pattern with a name, tags, priceId, and fileName(s)
// pointing to images in Firebase Storage under patternLibrary/.

import {
	collection,
	doc,
	getDocs,
	orderBy,
	query,
	setDoc,
	startAfter,
	limit as qLimit,
	where,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const LIBRARY_COLLECTION = 'library';
const PAGE_SIZE = 12;

// Fetches a single page of library patterns ordered by name.
// Pass a Firestore document snapshot as `cursor` to start after it (pagination).
// Returns { docs, lastDoc } where lastDoc can be used as the next cursor.
export const fetchLibraryPage = async (cursor = null) => {
	const base = collection(db, LIBRARY_COLLECTION);
	const constraints = [orderBy('name'), qLimit(PAGE_SIZE)];
	if (cursor) constraints.push(startAfter(cursor));

	const snap = await getDocs(query(base, ...constraints));
	const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
	const lastDoc = snap.docs[snap.docs.length - 1] ?? null;
	return { docs, lastDoc };
};

// Searches the library by name substring and/or tags.
// Returns a deduplicated, sorted array of matching pattern objects.
export const searchLibrary = async (searchTerm, tokens) => {
	const base = collection(db, LIBRARY_COLLECTION);
	const normalize = (s) => (s ?? '').toString().trim().toLowerCase();

	const term = normalize(searchTerm);

	// Name-ordered fetch for substring match
	const nameSnap = await getDocs(query(base, orderBy('name')));
	let docs = nameSnap.docs
		.map((d) => ({ id: d.id, ...d.data() }))
		.filter((d) => normalize(d.name).includes(term));

	// Tags array-contains-any search
	if (tokens.length > 0) {
		const tagsSnap = await getDocs(
			query(
				base,
				where('tags', 'array-contains-any', tokens.slice(0, 10)),
			),
		);
		docs = docs.concat(
			tagsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
		);
	}

	// Dedupe and sort
	const seen = new Map();
	for (const d of docs) seen.set(d.id, d);
	return [...seen.values()].sort((a, b) =>
		normalize(a.name).localeCompare(normalize(b.name)),
	);
};

// Writes a new pattern document to the library collection.
// patternData should include: name, tags, priceId, fileName (and optionally fileName2).
export const addLibraryPattern = (id, patternData) =>
	setDoc(doc(db, LIBRARY_COLLECTION, id), patternData);

// Fetches library pattern metadata by its fileName field.
// Used to look up price/tag info when a library image is loaded into the pattern maker.
export const fetchPatternByFileName = async (fileName) => {
	const snap = await getDocs(
		query(
			collection(db, LIBRARY_COLLECTION),
			where('fileName', '==', fileName),
		),
	);
	if (snap.empty) return null;
	return snap.docs[0].data();
};
